/**
 * Custom error types for referral code operations.
 * These follow the established pattern from ui/apps/web/src/utils/error.ts
 */

/**
 * Constants for referral error names and messages
 */
const REFERRAL_ERROR_NAMES = {
  DISPLAY: 'ReferralDisplayError',
  GENERIC: 'ReferralGenericError',
} as const

export const REFERRAL_ERROR_MESSAGES = {
  GENERIC_LINK_FAILED: 'Failed to link referral code',
} as const

/**
 * Error for user-displayable referral linking errors.
 * These errors contain messages that should be shown directly to the user.
 */
export class ReferralDisplayError extends Error {
  readonly name = REFERRAL_ERROR_NAMES.DISPLAY

  constructor(message: string, cause?: Error | string | undefined) {
    super(message, {
      cause,
    })
  }
}

/**
 * Error for generic referral linking failures.
 * These errors should show a generic message to the user.
 */
export class ReferralGenericError extends Error {
  readonly name = REFERRAL_ERROR_NAMES.GENERIC

  constructor(
    message: string = REFERRAL_ERROR_MESSAGES.GENERIC_LINK_FAILED,
    cause?: Error | string | undefined,
  ) {
    super(message, {
      cause,
    })
  }
}

/**
 * Type guard to check if an error is a ReferralDisplayError
 */
export function isReferralDisplayError(
  error: unknown,
): error is ReferralDisplayError {
  return (
    error instanceof ReferralDisplayError ||
    (error instanceof Error && error.name === REFERRAL_ERROR_NAMES.DISPLAY)
  )
}
